import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, ConfirmModal, Input, Table, getStatusVariant } from '@/components/ui';
import { useToast } from '@/components/ui/Toast';
import { useGateways, useDeleteGateway } from '@/hooks/useGateways';
import { useSpaceId } from '@/contexts/SpaceContext';
import { routes } from '@/lib/routes';
import type { Gateway } from '@/types';
import CreateGatewayModal from './CreateGatewayModal';

function formatRelativeTime(dateStr?: string): string {
  if (!dateStr) return 'Never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export default function GatewayList() {
  const navigate = useNavigate();
  const spaceId = useSpaceId();
  const { success, error: showError } = useToast();
  const { data, isLoading } = useGateways();
  const deleteGateway = useDeleteGateway();

  const [searchQuery, setSearchQuery] = useState('');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; gateway: Gateway | null }>({
    isOpen: false,
    gateway: null,
  });

  const gateways = data?.items || [];

  const filtered = useMemo(() => {
    if (!searchQuery) return gateways;
    const query = searchQuery.toLowerCase();
    return gateways.filter(
      (gw) =>
        gw.name.toLowerCase().includes(query) ||
        gw.endpoint.toLowerCase().includes(query)
    );
  }, [gateways, searchQuery]);

  const handleDelete = async () => {
    if (!deleteModal.gateway) return;
    try {
      await deleteGateway.mutateAsync(deleteModal.gateway.gatewayId);
      success('Gateway deleted successfully');
      setDeleteModal({ isOpen: false, gateway: null });
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to delete gateway');
    }
  };

  const columns = [
    {
      key: 'name',
      header: 'Name',
      render: (gw: Gateway) => (
        <div>
          <p className="font-medium text-foreground">{gw.name}</p>
          <p className="text-muted-foreground text-xs mt-0.5 truncate max-w-xs">{gw.endpoint}</p>
        </div>
      ),
    },
    {
      key: 'region',
      header: 'Region',
      render: (gw: Gateway) => (
        <span className="text-sm text-muted-foreground">{gw.region || '-'}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (gw: Gateway) => (
        <div className="flex items-center gap-2">
          <Badge variant={gw.enabled ? getStatusVariant(gw.status || 'active') : 'gray'}>
            {gw.enabled ? (gw.status || 'active') : 'disabled'}
          </Badge>
        </div>
      ),
    },
    {
      key: 'toolCount',
      header: 'Tools',
      render: (gw: Gateway) => (
        <span className="text-sm text-muted-foreground">{gw.toolCount}</span>
      ),
    },
    {
      key: 'lastSynced',
      header: 'Last Synced',
      render: (gw: Gateway) => (
        <span className="text-sm text-muted-foreground">
          {formatRelativeTime(gw.lastSyncedAt)}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (gw: Gateway) => (
        <div className="flex items-center gap-2 justify-end">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              setDeleteModal({ isOpen: true, gateway: gw });
            }}
            className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/30"
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex gap-4 items-center">
        <div className="flex-1 max-w-md">
          <Input
            placeholder="Search gateways..."
            value={searchQuery}
            onChange={(e: { target: { value: string } }) => setSearchQuery(e.target.value)}
          />
        </div>
        <Button onClick={() => setCreateModalOpen(true)}>Add Gateway</Button>
      </div>

      <Table
        columns={columns}
        data={filtered}
        keyExtractor={(gw) => gw.gatewayId}
        onRowClick={(gw) => navigate(routes.console.spaceToolsGateway(spaceId, gw.gatewayId))}
        isLoading={isLoading}
        emptyMessage={searchQuery ? 'No gateways match your search.' : 'No gateways configured. Add one to get started.'}
      />

      <CreateGatewayModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreated={(gw) => navigate(routes.console.spaceToolsGateway(spaceId, gw.gatewayId))}
      />

      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, gateway: null })}
        onConfirm={handleDelete}
        title="Delete Gateway"
        message={`Are you sure you want to delete "${deleteModal.gateway?.name}"? This will remove ${deleteModal.gateway?.toolCount ?? 0} synced tools and unassign them from any agents.`}
        confirmText="Delete"
        isLoading={deleteGateway.isPending}
      />
    </div>
  );
}
